import React from 'react'

import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react'

import { WebhookListItem } from '@/api/types'
import {
  useApiKeys,
  useDeleteWebhook,
  useRevokeApiKey,
  useUpdateWebhook,
  useWebhooks,
} from '@/react-query/api-settings'
import { useIsAdmin } from '@/react-query/team'

import { ConfirmActionModal } from './components/ConfirmActionModal'
import { CreateApiKeyModal } from './components/CreateApiKeyModal'
import { WebhookModal } from './components/WebhookModal'

const formatDate = (date: Date | string | null) => {
  if (!date) return 'Never'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date))
}

// --- API Keys Section ---

const ApiKeysSection = () => {
  const { data: apiKeys, isLoading } = useApiKeys()
  const revokeApiKey = useRevokeApiKey()
  const createModal = useDisclosure()
  const toast = useToast()

  const [revokeTarget, setRevokeTarget] = React.useState<{
    id: string
    name: string
  } | null>(null)

  if (isLoading) return null

  const activeKeys = apiKeys?.filter((k) => !k.revokedAt) || []

  return (
    <Box w="100%">
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md">API Keys</Heading>
        <Button variant="brand-solid" size="sm" onClick={createModal.onOpen}>
          Create API Key
        </Button>
      </Flex>

      {activeKeys.length === 0 ? (
        <Text color="gray.500">No API keys yet.</Text>
      ) : (
        <VStack spacing={3} align="stretch">
          {activeKeys.map((key) => (
            <Flex
              key={key.id}
              bg="white"
              border="1px solid"
              borderColor="gray.100"
              rounded="xl"
              px={5}
              py={4}
              justify="space-between"
              align="center"
            >
              <Box>
                <Text fontWeight="600">{key.name}</Text>
                <HStack spacing={3} mt={1.5}>
                  <Text fontSize="sm" color="gray.500" fontFamily="mono">
                    {key.prefix}...
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    ·
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Last used: {formatDate(key.lastUsedAt)}
                  </Text>
                  <Text fontSize="sm" color="gray.300">
                    ·
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    Created: {formatDate(key.createdAt)}
                  </Text>
                </HStack>
              </Box>
              <Button
                variant="brand-solid-red"
                size="sm"
                onClick={() => setRevokeTarget({ id: key.id, name: key.name })}
              >
                Revoke
              </Button>
            </Flex>
          ))}
        </VStack>
      )}

      <CreateApiKeyModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
      />

      <ConfirmActionModal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revoke API Key"
        message={`Are you sure you want to revoke the API key "${revokeTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Revoke"
        isPending={revokeApiKey.isPending}
        onConfirm={async () => {
          if (!revokeTarget) return
          try {
            await revokeApiKey.mutateAsync(revokeTarget.id)
            setRevokeTarget(null)
          } catch {
            toast({
              title: 'Failed to revoke API key',
              status: 'error',
              duration: 5000,
              isClosable: true,
            })
          }
        }}
      />
    </Box>
  )
}

// --- Webhooks Section ---

const WebhooksSection = () => {
  const { data: webhooks, isLoading } = useWebhooks()
  const updateWebhook = useUpdateWebhook()
  const deleteWebhook = useDeleteWebhook()
  const createModal = useDisclosure()
  const toast = useToast()

  const [editingWebhook, setEditingWebhook] =
    React.useState<WebhookListItem | null>(null)
  const [deleteTarget, setDeleteTarget] = React.useState<{
    id: string
    url: string
  } | null>(null)

  if (isLoading) return null

  return (
    <Box w="100%">
      <Flex align="center" justify="space-between" mb={4}>
        <Heading size="md">Webhooks</Heading>
        <Button variant="brand-solid" size="sm" onClick={createModal.onOpen}>
          Add Webhook
        </Button>
      </Flex>

      {!webhooks?.length ? (
        <Text color="gray.500">No webhooks configured.</Text>
      ) : (
        <VStack spacing={3} align="stretch">
          {webhooks.map((webhook) => (
            <Flex
              key={webhook.id}
              bg="white"
              border="1px solid"
              borderColor="gray.100"
              rounded="xl"
              px={5}
              py={4}
              justify="space-between"
              align="center"
            >
              <Box flex={1} minW={0}>
                <Text fontWeight="600" isTruncated>
                  {webhook.url}
                </Text>
                <HStack spacing={2} mt={1.5} flexWrap="wrap">
                  {webhook.events.map((event) => (
                    <Badge key={event} fontSize="xs" colorScheme="gray">
                      {event}
                    </Badge>
                  ))}
                </HStack>
              </Box>
              <HStack spacing={3} ml={4} flexShrink={0}>
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.500">
                    {webhook.isActive ? 'Active' : 'Inactive'}
                  </Text>
                  <Switch
                    size="sm"
                    isChecked={webhook.isActive}
                    onChange={() =>
                      updateWebhook.mutate(
                        {
                          webhookId: webhook.id,
                          isActive: !webhook.isActive,
                        },
                        {
                          onError: () =>
                            toast({
                              title: 'Failed to update webhook',
                              status: 'error',
                              duration: 5000,
                              isClosable: true,
                            }),
                        },
                      )
                    }
                  />
                </HStack>
                <Button
                  variant="brand-solid-white"
                  size="sm"
                  onClick={() => setEditingWebhook(webhook)}
                >
                  Edit
                </Button>
                <Button
                  variant="brand-solid-red"
                  size="sm"
                  onClick={() =>
                    setDeleteTarget({ id: webhook.id, url: webhook.url })
                  }
                >
                  Delete
                </Button>
              </HStack>
            </Flex>
          ))}
        </VStack>
      )}

      <WebhookModal isOpen={createModal.isOpen} onClose={createModal.onClose} />

      <WebhookModal
        isOpen={!!editingWebhook}
        onClose={() => setEditingWebhook(null)}
        webhook={editingWebhook || undefined}
      />

      <ConfirmActionModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Webhook"
        message={`Are you sure you want to delete the webhook for "${deleteTarget?.url}"?`}
        confirmLabel="Delete"
        isPending={deleteWebhook.isPending}
        onConfirm={async () => {
          if (!deleteTarget) return
          try {
            await deleteWebhook.mutateAsync(deleteTarget.id)
            setDeleteTarget(null)
          } catch {
            toast({
              title: 'Failed to delete webhook',
              status: 'error',
              duration: 5000,
              isClosable: true,
            })
          }
        }}
      />
    </Box>
  )
}

// --- Main Component ---

export const ApiSettings = () => {
  const isAdmin = useIsAdmin()

  if (!isAdmin) return null

  return (
    <VStack spacing={8} mt={16} alignItems={'flex-start'} w="100%" pb={8}>
      <Heading fontSize="2xl" fontWeight="bold">
        API & Integrations
      </Heading>
      <ApiKeysSection />
      <WebhooksSection />
    </VStack>
  )
}
